/**
 * The subscribe dialog's plan values and served quote (TMMAPP-0025; moved from the plan step in
 * TMMAPP-0116). Nothing here derives a price client-side.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EntitlementsQuote, PriceBook } from '@/api/entitlements';
import { entitlementsApi } from '@/api/entitlements';
import type { PromotionPreview } from '@/api/promotions';
import { hasAnnualOffer } from './planPrices';

export type QuoteStatus = 'loading' | 'ready' | 'error';
export type PlanInterval = 'MONTH' | 'YEAR';

export interface PlanValues {
  interval: PlanInterval;
  aiOn: boolean;
  promo: PromotionPreview | null;
  cardComplete: boolean;
  /** Hide Annual after INTERVAL_NOT_OFFERED. */
  annualHidden: boolean;
}

export function defaultPlanValues(aiEnabled = false): PlanValues {
  return {
    interval: 'MONTH',
    aiOn: aiEnabled,
    promo: null,
    cardComplete: false,
    annualHidden: false,
  };
}

/** Annual is on offer: the price book has it and the server has not refused it. */
export function planShowsAnnual(prices: PriceBook, values: PlanValues): boolean {
  return hasAnnualOffer(prices) && !values.annualHidden;
}

/**
 * The served quote for the chosen interval and promotion, debounced 400 ms; nothing is derived
 * client-side. `enabled` is false until the price book is in.
 *
 * `regularQuote` is the same interval quoted **without** the promotion (TMMAPP-0119 item 14): the
 * renewal price box 2 states. Without a code it is `quote`; with one, the interval's no-code quote
 * already in hand is kept, else it is fetched beside the code's. `retry` asks again after a failure
 * (item 15), so the blocking dialog never needs a reload.
 */
export function usePlanQuote(
  values: PlanValues,
  showAnnual: boolean,
  enabled: boolean,
): {
  quote: EntitlementsQuote | null;
  regularQuote: EntitlementsQuote | null;
  quoteStatus: QuoteStatus;
  quoteError: unknown;
  retry: () => void;
} {
  const [quote, setQuote] = useState<EntitlementsQuote | null>(null);
  const [regularQuote, setRegularQuote] = useState<EntitlementsQuote | null>(null);
  const [quoteStatus, setQuoteStatus] = useState<QuoteStatus>('loading');
  const [quoteError, setQuoteError] = useState<unknown>(undefined);
  const [attempt, setAttempt] = useState(0);
  const quoteGen = useRef(0);
  // The no-code quote per interval, kept while a code is applied.
  const regularByInterval = useRef(new Map<string, EntitlementsQuote>());
  const promotionCode = values.promo?.code?.trim() ?? '';
  const interval = values.interval;

  useEffect(() => {
    if (!enabled) return;
    const gen = ++quoteGen.current;
    const yearly = showAnnual && interval === 'YEAR';
    const intervalKey = yearly ? 'YEAR' : 'MONTH';
    const base = { seats: 0, connections: 0, ...(yearly ? { interval: 'YEAR' as const } : {}) };
    const timer = window.setTimeout(() => {
      setQuoteStatus('loading');
      const kept = regularByInterval.current.get(intervalKey) ?? null;
      const withCode =
        promotionCode.length > 0 ? entitlementsApi.quote({ ...base, promotionCode }) : null;
      const regular =
        withCode !== null && kept !== null ? Promise.resolve(kept) : entitlementsApi.quote(base);
      void Promise.all([withCode ?? regular, regular])
        .then(([next, nextRegular]) => {
          if (quoteGen.current !== gen) return;
          regularByInterval.current.set(intervalKey, nextRegular);
          setQuote(next);
          setRegularQuote(nextRegular);
          setQuoteError(undefined);
          setQuoteStatus('ready');
        })
        .catch((error: unknown) => {
          if (quoteGen.current !== gen) return;
          setQuoteError(error);
          setQuoteStatus('error');
        });
    }, 400);
    return () => {
      window.clearTimeout(timer);
      // A quote still in flight when the inputs change or the step unmounts is stale.
      quoteGen.current += 1;
    };
  }, [enabled, promotionCode, interval, showAnnual, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { quote, regularQuote, quoteStatus, quoteError, retry };
}

/**
 * The base and first-charge amounts as served. A YEAR selection is priced only from
 * `quote.annual`. Never from the monthly figures, and never from the price book.
 * `annualOmitted` is a ready quote with no annual offer, or a quote already in hand that
 * left it out (a refetch still in flight). The first load has no quote yet.
 */
export function planAmounts(
  quote: EntitlementsQuote | null,
  quoteStatus: QuoteStatus,
  showAnnual: boolean,
  values: PlanValues,
  fallbackCurrency: string,
): {
  isYear: boolean;
  baseMinor: number | null;
  totalMinor: number | null;
  currency: string;
  ready: boolean;
  annualOmitted: boolean;
} {
  const annual = quote?.annual ?? null;
  const ready = quoteStatus === 'ready' && quote != null;
  const annualOmitted = quoteStatus === 'ready' ? annual == null : quote != null && annual == null;
  const pricingYear = showAnnual && values.interval === 'YEAR' && !annualOmitted;
  const isYear = pricingYear && annual != null;
  const baseMinor = pricingYear ? (annual?.baseMinor ?? null) : (quote?.baseMinor ?? null);
  const totalMinor = pricingYear
    ? (annual?.totalFirstYearMinor ?? null)
    : (quote?.totalMonthlyMinor ?? null);
  const currency = quote?.currency || fallbackCurrency;
  return {
    isYear,
    baseMinor,
    totalMinor,
    currency,
    ready,
    annualOmitted,
  };
}

/** The trial the quote serves; `null` while it is loading or failed. */
export function servedTrialDays(
  quote: EntitlementsQuote | null,
  quoteStatus: QuoteStatus,
): number | null {
  if (quoteStatus !== 'ready' || quote == null) return null;
  return quote.trialDays;
}
